import { PositionalAudio, useTexture } from '@react-three/drei';
import type { ThreeElements } from '@react-three/fiber';
import { folder, useControls } from 'leva';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import {
  color,
  distance,
  float,
  Fn,
  normalLocal,
  positionLocal,
  smoothstep,
  texture,
  time,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import { simplexNoise, worleyNoiseFunction } from '../utils/tsl-noise';
import useGameStore from '../store/useGameStore';

type WaterFallProps = ThreeElements['group'] & {
  waterFall_geo: THREE.BufferGeometry;
  waterfall_material: THREE.MeshStandardMaterial;

  stream_material_geo: THREE.BufferGeometry;
  stream_material: THREE.MeshStandardMaterial;

  waterfallEdge_geo: THREE.BufferGeometry;
  waterfall_edge_material: THREE.MeshStandardMaterial;

  river_geo: THREE.BufferGeometry;
  river_material: THREE.MeshStandardMaterial;
};

export const Waterfall = ({
  waterFall_geo,
  stream_material_geo,
  waterfallEdge_geo,
  river_geo,
  ...groupProps
}: WaterFallProps) => {
  const streamRef = useRef<ThreeElements['meshBasicNodeMaterial']>(null!);
  const waterFallRef = useRef<ThreeElements['meshBasicNodeMaterial']>(null!);
  const waterEdgeRef = useRef<ThreeElements['meshBasicNodeMaterial']>(null!);
  const riverRef = useRef<ThreeElements['meshBasicNodeMaterial']>(null!);
  const splashRef = useRef<ThreeElements['meshBasicNodeMaterial']>(null!);

  /** -------------
      State management
     ----------------*/
  const playAudio = useGameStore((state) => state.playAudio);

  /** -------------
          Controls
     ----------------*/
  const controls = useControls(
    'water',
    {
      waterColor: {
        value: '#0aacff',
      },
      stream: folder(
        {
          streamNoiseScale: {
            value: 2.05,
            min: 0,
            max: 10,
            step: 0.01,
          },
          streamNoiseSeed: {
            value: 2.4,
            min: 0,
            max: 10,
            step: 0.01,
          },
          streamDepth: {
            value: 0.14,
            min: 0.01,
            max: 2,
            step: 0.01,
          },
        },
        { collapsed: true }
      ),
      waterfall: folder(
        {
          waterfallNoiseScale: {
            value: 1.73,
            min: 0,
            max: 10,
            step: 0.01,
          },
          waterfallNoiseSeed: {
            value: 3.26,
            min: 0,
            max: 10,
            step: 0.01,
          },
          waterfallDepth: {
            value: 0.24,
            min: 0.01,
            max: 2,
            step: 0.01,
          },
        },
        { collapsed: true }
      ),
      edgeWater: folder(
        {
          edgeWaterNoiseScale: {
            value: 3.07,
            min: 0,
            max: 10,
            step: 0.01,
          },
          edgeWaterNoiseSeed: {
            value: 3.26,
            min: 0,
            max: 10,
            step: 0.01,
          },
          edgeWaterDepth: {
            value: 0.89,
            min: 0.01,
            max: 2,
            step: 0.01,
          },
        },
        { collapsed: true }
      ),
      splashWaterWater: folder(
        {
          splashWaterNoiseScale: {
            value: 1.77,
            min: 0,
            max: 10,
            step: 0.01,
          },
          splashWaterNoiseSeed: {
            value: 1.35,
            min: 0,
            max: 10,
            step: 0.01,
          },
          splashWaterDepth: {
            value: 1.08,
            min: 0.01,
            max: 2,
            step: 0.01,
          },
          noiseColorA: {
            //   value: '#6c6b6b',
            value: '#ebb6b6',
          },
          noiseColorB: {
            value: '#000000',
          },
          positionX: {
            value: -19,
            min: -20,
            max: 10,
          },
          positionY: {
            value: 0.7,
            min: 0,
            max: 10,
          },
          positionZ: {
            value: -0.5,
            min: -5,
            max: 10,
          },
          uDisplacement: {
            value: 1.58,
            min: -1,
            max: 5,
          },
          uIntensity: {
            value: 2.76,
            min: -1,
            max: 5,
          },
          uErasure: {
            value: -1,
            min: -5,
            max: 5,
          },
        },
        { collapsed: true }
      ),
    },
    { collapsed: true }
  );

  const [perlinNoiseTexture] = useTexture(['/texture/noise/perlin2.png']);
  perlinNoiseTexture.wrapS = perlinNoiseTexture.wrapT = THREE.RepeatWrapping;

  const { shaderNode } = useMemo(() => {
    const waterColor = uniform(color(controls.waterColor));
    const noiseFreq = 2;
    const perlinNoiseTextureUV = texture(
      perlinNoiseTexture,
      uv().mul(noiseFreq)
    );
    //stream shader code
    const streamColorNode = Fn(() => {
      const fWorl = worleyNoiseFunction(
        uniform(controls.streamNoiseScale),
        float(uniform(controls.streamNoiseSeed)).add(time.mul(-0.4)),
        uniform(controls.streamDepth)
      );

      return vec4(vec3(fWorl.x).mul(3).add(waterColor), 0.0);
    })();

    const waterFallColorNode = Fn(() => {
      const fWorl = worleyNoiseFunction(
        uniform(controls.waterfallNoiseScale, 'float'),
        uniform(controls.waterfallNoiseSeed, 'float').add(time.mul(1.2)),
        uniform(controls.waterfallDepth, 'float')
      );

      return vec4(vec3(fWorl.x).mul(2).add(waterColor), 0.0);
    })();

    const waterEdgeColorNode = Fn(() => {
      const fWorl = worleyNoiseFunction(
        uniform(controls.edgeWaterNoiseScale),
        float(uniform(controls.edgeWaterNoiseSeed)).add(time.mul(2)),
        uniform(controls.edgeWaterDepth)
      );

      const distanceFn = distance(uv(), vec2(0.45, 0.5)).oneMinus();

      const smoothen = smoothstep(0.5, 1.0, distanceFn).mul(vec3(fWorl).mul(2));

      return vec4(vec3(smoothen.x).add(waterColor), smoothen.x);
    })();

    const riverColorNode = Fn(() => {
      const ripple = distance(uv(), vec2(0.04, 0.5)).oneMinus();
      const rippleRepeatFrequency = 10.0;
      const rippleTimeFrequency = 0.05;

      ripple.assign(
        ripple
          .add(time.mul(rippleTimeFrequency))
          .mul(rippleRepeatFrequency)
          .mod(1)
          .sub(ripple.oneMinus())
          .add(perlinNoiseTextureUV)
      );

      const rippleIndex = ripple
        .add(time.mul(rippleTimeFrequency))
        .mul(rippleRepeatFrequency)
        .floor();

      const noise = texture(
        perlinNoiseTexture,
        uv().add(rippleIndex.div(0.345)).mul(noiseFreq)
      ).r;

      // const threshhold = 1;
      // ripple.greaterThan(threshhold).discard();

      return vec4(vec3(ripple.mul(noise)).add(waterColor), 1);
    })();

    const simplex = simplexNoise(
      uniform(controls.uDisplacement, 'float'), //displacement
      time.mul(4),
      uniform(controls.uIntensity, 'float'), //intensity
      uniform(controls.uErasure, 'float'), //erasure
      uniform(color(controls.noiseColorA)),
      uniform(color(controls.noiseColorB))
    );

    // const displaceFn = positionLocal.add(simplex.mul(normalLocal));

    const splashColorNode = Fn(() => {
      const displaceColor = vec4(simplex);
      return vec4(0.3, 0.7, 0.9, displaceColor.r);
    })();

    const splashPosNode = Fn(() => {
      const displaceFn = positionLocal.add(simplex.mul(normalLocal));

      return displaceFn;
    })();

    // update materials on save
    if (waterEdgeRef.current) {
      streamRef.current.needsUpdate = true;
      waterFallRef.current.needsUpdate = true;
      waterEdgeRef.current.needsUpdate = true;
      riverRef.current.needsUpdate = true;
      splashRef.current.needsUpdate = true;
    }
    return {
      shaderNode: {
        streamColorNode,
        riverColorNode,
        waterFallColorNode,
        waterEdgeColorNode,
        splashPosNode,
        splashColorNode,
      },
    };
  }, [controls, perlinNoiseTexture]);

  return (
    <group {...groupProps}>
      <mesh geometry={stream_material_geo} receiveShadow castShadow>
        <meshPhongNodeMaterial
          ref={streamRef}
          colorNode={shaderNode.streamColorNode}
        />
      </mesh>

      <mesh geometry={waterfallEdge_geo} receiveShadow castShadow>
        <meshPhongNodeMaterial
          ref={waterEdgeRef}
          colorNode={shaderNode.waterEdgeColorNode}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      <mesh
        geometry={waterFall_geo}
        position={[0, 0.05, 0]}
        receiveShadow
        castShadow
      >
        <meshPhongNodeMaterial
          ref={waterFallRef}
          colorNode={shaderNode.waterFallColorNode}
        />
      </mesh>

      <mesh geometry={river_geo} receiveShadow castShadow>
        <meshPhongNodeMaterial
          ref={riverRef}
          colorNode={shaderNode.riverColorNode}
        />
      </mesh>

      <mesh
        position={[controls.positionX, controls.positionY, controls.positionZ]}
        scale={[0.1, 0.7, 0.9]}
      >
        <icosahedronGeometry args={[1.5, 3]} />
        <meshPhongNodeMaterial
          ref={splashRef}
          colorNode={shaderNode.splashColorNode}
          positionNode={shaderNode.splashPosNode}
          transparent={true}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {playAudio && (
        <PositionalAudio autoplay loop url='/audio/water.mp3' distance={7} />
      )}
    </group>
  );
};
